import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faPen, faPaperPlane, faCheck, faBan, faPlus, faFilePdf, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import DataService from '../../services/DataService';
import BillService from '../../services/BillService';
import TimeStamp from '../../utils/TimeStamp';
import BillLineModal from './BillLineModal';
import PDFDocumentService from '../../services/PDFDocumentService';
import DataDocumentService from '../../services/DataDocumentService';
import Tooltip from '../../components/common/Tooltip';


const BillList = () => {
    const [bills, setBills] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [billStatuses, setBillStatuses] = useState({});
    const [selectedBillId, setSelectedBillId] = useState(null);
    const [isModalActive, setIsModalActive] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const pageSize = 10;


    // Charger les statuts de factures
    const fetchBillStatuses = useCallback(async () => {
        try {
            const statuses = await DataService.getTypes('BillStatus');
            const statusMap = {};
            statuses.forEach(status => {
                statusMap[status.name] = status.data;
            });
            setBillStatuses(statusMap);
        } catch (error) {
            console.error('Erreur lors de la récupération des statuts:', error);
        }
    }, []);

    const fetchBills = useCallback(async () => {
        try {
            const [bills, pages] = await Promise.all([
                DataService.getAll('bills', currentPage, pageSize),
                DataService.getTotalPages('bills', pageSize)
            ]);

            const billsWithDetails = await Promise.all(bills.map(async (bill) => {
                const quote = await DataService.getById('quotes', bill.quoteId);
                if (!quote) return null;

                const opportunity = await DataService.getById('opportunities', quote.opportunityId);
                if (!opportunity) return null;

                const account = await DataService.getById('accounts', opportunity.accountId);
                if (!account) return null;

                return {
                    id: bill.id,
                    accountName: account.name,
                    opportunityName: opportunity.name,
                    billCustomId: bill.billCustomId,
                    status: bill.status,
                    timeStamp: bill.timeStamp,
                    totalAmount: bill.totalAmount,
                    currentBillAmount: bill.currentBillAmount,
                    currentProgressAmount: bill.currentProgressAmount,
                    quoteId: bill.quoteId,
                    isLastBill: bill.isLastBill
                };
            }));

            setBills(billsWithDetails.filter(bill => bill !== null));
            setTotalPages(pages);
        } catch (error) {
            console.error('Erreur lors de la récupération des factures:', error);
        }
    }, [currentPage]);

    useEffect(() => {
        fetchBillStatuses();
        fetchBills();
    }, [fetchBills, fetchBillStatuses]);

    // Fonction pour formater la date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        // Convertir l'objet JSON TimeStamp en instance TimeStamp
        const timeStampInstance = TimeStamp.fromString(`${timestamp.year}-${timestamp.month}-${timestamp.day}-${timestamp.hour}-${timestamp.minute}`);
        return timeStampInstance.toFormattedDate();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleStatusChange = async (billId, newStatus) => {
        try {
            const bill = await DataService.getById('bills', billId);
            await DataService.update('bills', billId, { status: newStatus });

            // Si on passe au statut "Paid" et que le montant total est atteint
            if (newStatus === 'Paid' && bill.totalAmount === bill.currentProgressAmount) {
                const quote = await DataService.getById('quotes', bill.quoteId);
                if (quote) {
                    await DataService.update('quotes', quote.id, { 
                        ...quote,
                        paid: true 
                    });
                }
            }

            fetchBills(); // Refresh the list
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
        }
    };

    const handleGenerateNewBill = async (bill) => {
        try {
            const quote = await DataService.getById('quotes', bill.quoteId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }
            await BillService.createBillFromQuote(quote);
            fetchBills(); // Refresh the list
        } catch (error) {
            console.error('Erreur lors de la génération de la facture:', error);
        }
    };

    const handleEditLines = async (billId) => {
        if (!billId) {
            console.error('BillId invalide');
            return;
        }
        setSelectedBillId(billId);
        setIsModalActive(true);
    };

    const handleCloseModal = () => {
        setIsModalActive(false);
        setSelectedBillId(null);
        fetchBills();
    };

    // Ajout de la fonction pour déterminer la classe de couleur du statut
    const getStatusColorClass = (status) => {
        switch (status) {
            case 'Paid':
                return 'has-text-success';
            case 'Canceled':
                return 'has-text-danger';
            default:
                return '';
        }
    };

    const handleGeneratePDF = async (billCustomId) => {
        try {
            const dataService = new DataDocumentService();
            const data = await dataService.getBillDocumentData(billCustomId);
            // Générer le PDF
            const pdfService = new PDFDocumentService();
            pdfService.generatePDF(data, 'INVOICE');
        } catch (error) {
            console.error('Erreur lors de la génération du PDF:', error);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedBills = () => {
        if (!sortConfig.key) return bills;

        return [...bills].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'account':
                    aValue = a.accountName;
                    bValue = b.accountName;
                    break;
                case 'project':
                    aValue = a.opportunityName;
                    bValue = b.opportunityName;
                    break;
                case 'billNumber':
                    aValue = a.billCustomId;
                    bValue = b.billCustomId;
                    break;
                case 'date':
                    // Convertir les TimeStamps en dates pour la comparaison
                    aValue = a.timeStamp ? new Date(
                        parseInt(a.timeStamp.year),
                        parseInt(a.timeStamp.month) - 1,
                        parseInt(a.timeStamp.day)
                    ) : new Date(0);
                    bValue = b.timeStamp ? new Date(
                        parseInt(b.timeStamp.year),
                        parseInt(b.timeStamp.month) - 1,
                        parseInt(b.timeStamp.day)
                    ) : new Date(0);
                    break;
                case 'totalAmount':
                    aValue = a.totalAmount || 0;
                    bValue = b.totalAmount || 0;
                    break;
                case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };


    return (
        <div className="panel">
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faFileInvoice} />
                        </span>
                        <span> Factures</span>
                    </span>
                    <div className="panel-header-actions">
                        <div className="buttons">
                            <button
                                className="button is-small"
                                onClick={() => handlePageChange(0)}
                                disabled={currentPage === 0}
                            >
                                <span className="icon is-small">««</span>
                            </button>
                            <button
                                className="button is-small"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                <span className="icon is-small">«</span>
                            </button>
                            <button
                                className="button is-small"
                                title={`Page ${currentPage + 1} sur ${totalPages}`}
                            >
                                <span className="icon is-small">{currentPage + 1}</span>
                            </button>
                            <button
                                className="button is-small"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                <span className="icon is-small">»</span>
                            </button>
                            <button
                                className="button is-small"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                <span className="icon is-small">»»</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', width: '100%' }}>
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead className="modal-table-header">
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('account')}
                                    >
                                        Compte <FontAwesomeIcon icon={getSortIcon('account')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('project')}
                                    >
                                        Projet <FontAwesomeIcon icon={getSortIcon('project')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('billNumber')}
                                    >
                                        N° Facture <FontAwesomeIcon icon={getSortIcon('billNumber')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('date')}
                                    >
                                        Date <FontAwesomeIcon icon={getSortIcon('date')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('totalAmount')}
                                    >
                                        Devis (€) <FontAwesomeIcon icon={getSortIcon('totalAmount')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">Facture (€)</th>
                                <th className="has-text-right">Progression</th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('status')}
                                    >
                                        Statut <FontAwesomeIcon icon={getSortIcon('status')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">
                                    <Tooltip content={
                                        <div>
                                            <strong>Workflow des actions par statut</strong>
                                            <div className="workflow-columns">
                                                <div className="workflow-column">
                                                    <h4>Brouillon</h4>
                                                    <ul>
                                                        <li>• Éditer les lignes</li>
                                                        <li>• Envoyer (si montant &gt; 0)</li>
                                                    </ul>
                                                </div>
                                                <div className="workflow-column">
                                                    <h4>Envoyé</h4>
                                                    <ul>
                                                        <li>• Marquer comme payée</li>
                                                        <li>• Annuler</li>
                                                    </ul>
                                                </div>
                                                <div className="workflow-column">
                                                    <h4>Payé / Annulé</h4>
                                                    <ul>
                                                        <li>• Nouvelle facture (si solde restant)</li>
                                                        <li>• Générer PDF (si montant &gt; 0)</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    }>
                                        Actions
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedBills().map(bill => (
                                <tr key={bill.id}>
                                    <td>{bill.accountName}</td>
                                    <td>{bill.opportunityName}</td>
                                    <td>{bill.billCustomId}</td>
                                    <td>{formatDate(bill.timeStamp)}</td>
                                    <td className="has-text-right">{bill.totalAmount?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</td>
                                    <td className="has-text-right">{bill.currentBillAmount?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</td>
                                    <td className="has-text-right">{bill.currentProgressAmount?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</td>
                                    <td className={getStatusColorClass(bill.status)}>{billStatuses[bill.status] || bill.status}</td>
                                    <td>
                                        <div className="table-actions-container">
                                            <div className="buttons are-small">
                                                {/* Edit button - visible only when status is Draft */}
                                                {bill.status === 'Draft' && (
                                                    <button
                                                        className="button is-small table-action-button"
                                                        onClick={() => handleEditLines(bill.id)}
                                                        title="Éditer les lignes"
                                                    >
                                                        <span className="icon icon-primary">
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </span>
                                                    </button>
                                                )}
                                                {/* Send button - visible when status is Draft */}
                                                {bill.status === 'Draft' && bill.currentBillAmount !== null && bill.currentBillAmount > 0.00  && (
                                                    <button
                                                        className="button is-small table-action-button"
                                                        onClick={() => handleStatusChange(bill.id, 'Sent')}
                                                        title="Envoyer"
                                                    >
                                                        <span className="icon is-small icon-primary">
                                                            <FontAwesomeIcon icon={faPaperPlane} />
                                                        </span>
                                                    </button>
                                                )}
                                                {/* Paid button - visible when status is Sent */}
                                                {bill.status === 'Sent' && (
                                                    <button
                                                        className="button is-small table-action-button"
                                                        onClick={() => handleStatusChange(bill.id, 'Paid')}
                                                        title="Payée"
                                                    >
                                                        <span className="icon icon-success">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </span>
                                                    </button>
                                                )}
                                                {/* Cancel button - visible when status is Sent */}
                                                {bill.status === 'Sent' && (
                                                    <button
                                                        className="button is-small table-action-button"
                                                        onClick={() => handleStatusChange(bill.id, 'Canceled')}
                                                        title="Annulée"
                                                    >
                                                        <span className="icon icon-danger">
                                                            <FontAwesomeIcon icon={faBan} />
                                                        </span>
                                                    </button>
                                                )}
                                                {/* Generate new bill button - visible when status is Paid/Canceled and amounts don't match */}
                                                {(bill.status === 'Paid' || bill.status === 'Canceled') 
                                                && bill.totalAmount !== bill.currentProgressAmount
                                                && bill.isLastBill                
                                                && (
                                                    <button
                                                        className="button is-small table-action-button"
                                                        onClick={() => handleGenerateNewBill(bill)}
                                                        title="Générer la facture"
                                                    >
                                                        <span className="icon is-small icon-primary">
                                                            <FontAwesomeIcon icon={faPlus} />
                                                        </span>
                                                    </button>
                                                )}
                                                {bill.currentBillAmount !== null && bill.currentBillAmount > 0.00  && (
                                                    <>
                                                        <button
                                                            className="button is-small table-action-button"
                                                            onClick={() => handleGeneratePDF(bill.billCustomId)}
                                                            title="Générer le PDF"
                                                        >
                                                            <span className="icon is-small icon-success">
                                                                <FontAwesomeIcon icon={faFilePdf} />
                                                            </span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="panel-footer"></div>

            <BillLineModal
                isActive={isModalActive}
                onClose={handleCloseModal}
                billId={selectedBillId}
            />
        </div>
    );
};

export default BillList;